import { useTranslation } from 'react-i18next'
import { FileText, Images, Pencil, Sparkles, RefreshCw, Plus, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/v2/ui'
import { formatDateTime } from '@/lib/utils'
import { EID } from '@/lib/element-ids.registry'
import { getBlockTypeInfo } from '@/hooks/useContentBlock'
import type { HistoryRow, HistoryBadge } from './changeEventToRow'

const BADGE_ICON: Record<HistoryBadge, typeof Pencil> = {
  created: Plus,
  ai: Sparkles,
  sync: RefreshCw,
  edited: Pencil,
}

interface Props {
  row: HistoryRow
  onOpen: (row: HistoryRow) => void
}

export function HistoryEntryRow({ row, onOpen }: Props) {
  const { t } = useTranslation()
  const TrackIcon = row.track === 'asset' ? Images : FileText
  const BadgeIcon = BADGE_ICON[row.badge]
  const title =
    row.track === 'asset'
      ? t('productHistory.imageTitle', { slot: row.slot ?? '?' })
      : row.blockType
        ? getBlockTypeInfo(row.blockType).label
        : t('productHistory.title')

  return (
    <button
      type="button"
      id={`${EID['product-history-entry']}-${row.id}`}
      onClick={() => onOpen(row)}
      className="w-full text-left rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
    >
      <div className="flex gap-3 items-start">
        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
          <TrackIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-neutral-900 dark:text-neutral-50">{title}</span>
            <Badge variant="secondary">
              <span className="inline-flex items-center gap-1">
                <BadgeIcon className="h-3 w-3" aria-hidden="true" />
                {t(`productHistory.badge.${row.badge}`)}
              </span>
            </Badge>
            {row.versionNumber != null && (
              <Badge variant="secondary">V{row.versionNumber}</Badge>
            )}
            <span className="text-sm text-neutral-500">· {row.scope}</span>
          </div>
          {row.summary && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 truncate">
              {row.summary}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-500 flex-shrink-0">
          <span>{formatDateTime(row.timestamp)}</span>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </div>
      </div>
    </button>
  )
}
